
import { getCookie, setCriticalCookie } from "@/lib/auth/cookies";
import { Event } from "../../../types/Event";
import {
  getUser as getUserApi,
  getUserDTO as getUserDTOApi,
  updateUserAttributes,
  requestAccountDeletion as requestAccountDeletionApi,
  confirmAccountDeletion as confirmAccountDeletionApi,
  getAccountDeletionPreflight as getAccountDeletionPreflightApi,
  type AccountDeletionPreflight,
} from "@/lib/api/clients/UserClient";
import { getPaginatedUserLogs, numUnreadLogs } from "@/lib/api/clients/UserLogClient";
import {
  InitialiseUserResponse,
  InitialiseUserBaseResponse,
} from "@/types/responses/UserResponses";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import type { LossesAck } from "@/lib/legal/dissolveLossesAck";
import { getEvent, getTwentyOneValidEventNames } from "@/lib/api/clients/EventClient";
import { UserDTO } from "@/types/UserDTO";
import { User } from "@/types/User";

export async function updateUser(user: User): Promise<void | ServerError> {
  try {

    // Call API to update user attributes
    await updateUserAttributes(user);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Server-side user handling functions
 * This file contains server actions for user authentication and data operations
 */
export async function getUserDTO(): Promise<UserDTO | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) {
      return handleErrorServer("Company ID is not set in cookies.");
    }

    return await getUserDTOApi(companyId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getUser(fields: string): Promise<User | ServerError> {
  try {
    return await getUserApi(fields);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Initializes the event-INDEPENDENT appbar data (user, notifications, event
 * list). Deliberately omits the selected event so this can be cached under an
 * eventId-less query key and survive event switches without refetching. The
 * selected event's name is threaded separately from the layout shell's
 * already-fetched event — no duplicate getEvent.
 * @returns Promise containing base user data or a ServerError
 */
export async function initialiseUserBase({
  fetchDate,
}: {
  fetchDate: Date;
}): Promise<InitialiseUserBaseResponse | ServerError> {
  try {
    let recentEventIds: string[] = JSON.parse(
      (await getCookie("recentEventIds")) || "[]"
    );

    // Ensure recentEventIds is an array
    if (!Array.isArray(recentEventIds)) {
      recentEventIds = [];
    }

    // Fetch all required data in parallel for better performance
    const [numUnread, notifications, notificationsUnread, user, events] =
      await Promise.all([
        numUnreadLogs(),
        getPaginatedUserLogs(0, 10, fetchDate),
        getPaginatedUserLogs(0, 10, fetchDate, false),
        getUserApi("monogramColor,firstName,email"),
        getTwentyOneValidEventNames(recentEventIds),
      ]);

    // Use explicit null/undefined checks — `numUnread` can legitimately be 0
    // for a fresh user, and arrays can be empty. Truthy `!` rejected those.
    if (
      numUnread === null ||
      numUnread === undefined ||
      notifications == null ||
      notificationsUnread == null ||
      user == null ||
      events == null
    )
      throw new Error("unknown");

    return {
      numUnread: numUnread,
      notifications: notifications,
      notificationsUnread: notificationsUnread,
      user,
      events,
    };
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Initializes user data by fetching necessary information for the dashboard
 * Retrieves user profile, notifications, and unread log status
 * @returns Promise containing user initialization data or a ServerError
 * @deprecated Prefer `initialiseUserBase` + threading the event from the
 *   layout shell. Retained for any non-shell caller that still needs the
 *   bundled event in one call.
 */
export async function initialiseUser({
  fetchDate,
  eventId,
}: {
  fetchDate: Date;
  eventId?: string;
}): Promise<InitialiseUserResponse | ServerError> {
  try {
    const eventPromise: Promise<Event | null> = eventId
      ? getEvent(eventId, "name")
      : Promise.resolve(null);

    const [base, event] = await Promise.all([
      initialiseUserBase({ fetchDate }),
      eventPromise,
    ]);

    if ("isServerError" in base) throw new Error("unknown");
    if (eventId && event == null) throw new Error("unknown");

    return { ...base, event };
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getUserDeletionPreflight(): Promise<
  AccountDeletionPreflight | ServerError
> {
  try {
    return await getAccountDeletionPreflightApi();
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function requestUserDeletion(
  successorUserId: string | null | undefined,
  // PB-CDP-7: set exactly when the dialog showed a loss notice AND the user
  // ticked the box under it.
  lossesAck: LossesAck | null,
): Promise<void | ServerError> {
  try {
    await setCriticalCookie("otpResend", `${Date.now() + 99 * 1000}`);
    await requestAccountDeletionApi(successorUserId, lossesAck);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function confirmUserDeletion(
  otp: string,
  successorUserId: string | null | undefined,
  // PB-CDP-7: the request's acknowledgement again — the confirm door
  // re-checks the losses and records it.
  lossesAck: LossesAck | null,
): Promise<boolean | ServerError> {
  if (!otp || otp.trim() === "") {
    return handleErrorServer("OTP is required for account deletion.");
  }

  try {
    return await confirmAccountDeletionApi(otp, successorUserId, lossesAck);
  } catch (error) {
    return handleErrorServer(error);
  }
}
